/**
 * Quiz configuration — questions, scoring, category mapping, and practices.
 */

export const CATEGORIES = {
  foundation: {
    title: "Communication",
    subtitle: "Foundation",
    icon: "\u{1F4AC}",
    description: "How safely and openly you share, listen, and resolve together.",
  },
  quick_connect: {
    title: "Daily Connection",
    subtitle: "Quick Connect",
    icon: "\u26A1",
    description: "How present and connected you feel in everyday moments.",
  },
  sensory_sync: {
    title: "Intimacy",
    subtitle: "Sensory Sync",
    icon: "\u{1F525}",
    description: "Physical closeness, desire, and feeling wanted by your partner.",
  },
  grand_vision: {
    title: "Shared Vision",
    subtitle: "Grand Vision",
    icon: "\u{1F680}",
    description: "Alignment on your future, values, and where you're headed together.",
  },
};

export const CATEGORY_ORDER = [
  "foundation",
  "quick_connect",
  "sensory_sync",
  "grand_vision",
];

export const PRACTICES = {
  foundation: {
    title: "2-Minute Truth Share",
    body:
      "Set a timer for 2 minutes each. One partner shares honestly how they feel (no blaming, just truth). The other partner can only listen—no interrupting, fixing, or reacting. Then switch.",
  },
  quick_connect: {
    title: "10-Minute No-Phone Connection",
    body:
      "Set aside 10 minutes with no phones, distractions, or logistics talk. Just be present and ask each other simple, real questions about your day or feelings.",
  },
  sensory_sync: {
    title: "Slow Touch Reset",
    body:
      "Spend 5 minutes touching (holding hands, hugging, or gentle contact) with no goal of sex. Just focus on presence and feeling each other.",
  },
  grand_vision: {
    title: "Future Snapshot",
    body:
      "Each of you describes your ideal life together 3 years from now. Where are you? What are you building? Then find 2–3 things that overlap.",
  },
};

export const QUESTIONS = [
  // SECTION 1 — CONTEXT
  {
    id: "q1", number: 1, section: "Context",
    prompt: "What best describes you?",
    options: [
      { label: "Man", value: "Man" },
      { label: "Woman", value: "Woman" },
    ],
  },
  {
    id: "q2", number: 2, section: "Context",
    prompt: "What is your current relationship status?",
    options: [
      { label: "Dating", value: "Dating" },
      { label: "Engaged", value: "Engaged" },
      { label: "Married (0–5 years)", value: "Married (0–5 years)" },
      { label: "Married (5+ years)", value: "Married (5+ years)" },
    ],
  },
  {
    id: "q3", number: 3, section: "Context",
    prompt: "How committed are you to improve your relationship?",
    options: [
      { label: "Yes, and I\u2019m committed to making it better", value: "Committed" },
      { label: "Committed, but it feels hard", value: "Hard right now" },
      { label: "Not really sure anymore", value: "Unsure" },
    ],
  },
  // SECTION 2 — SURFACE AWARENESS (Q4 has category weighting, no score)
  {
    id: "q4", number: 4, section: "Surface Awareness",
    prompt: "What are you struggling with MOST right now?",
    options: [
      { label: "Communication & feeling heard", value: "Communication", weightCategory: "foundation" },
      { label: "Emotional connection / vulnerability", value: "Emotional connection", weightCategory: "foundation" },
      { label: "Intimacy / physical connection", value: "Intimacy", weightCategory: "sensory_sync" },
      { label: "Feeling aligned on the future", value: "Future alignment", weightCategory: "grand_vision" },
    ],
  },
  // FOUNDATION
  {
    id: "q7", number: 7, section: "Foundation", category: "foundation",
    prompt: "How easy is it for you to express what you really feel?",
    options: [
      { label: "Very easy", value: "Very easy", score: 3 },
      { label: "Somewhat easy", value: "Somewhat easy", score: 2 },
      { label: "Difficult", value: "Difficult", score: 1 },
      { label: "I avoid it", value: "I avoid it", score: 0 },
    ],
  },
  {
    id: "q8", number: 8, section: "Foundation", category: "foundation",
    prompt: "Do you feel safe being fully honest with your partner?",
    options: [
      { label: "Yes, completely", value: "Completely", score: 3 },
      { label: "Mostly", value: "Mostly", score: 2 },
      { label: "Not really", value: "Not really", score: 1 },
      { label: "Not at all", value: "No", score: 0 },
    ],
  },
  {
    id: "q9", number: 9, section: "Foundation", category: "foundation",
    prompt: "When something is bothering you, what usually happens?",
    options: [
      { label: "I bring it up calmly and we talk it through", value: "Talk calmly", score: 3 },
      { label: "I hint at it, hoping they\u2019ll notice", value: "Hint", score: 2 },
      { label: "I keep it in until it comes out in frustration", value: "Keep in", score: 1 },
      { label: "I avoid it completely", value: "Avoid", score: 0 },
    ],
  },
  // QUICK CONNECT
  {
    id: "q10", number: 10, section: "Quick Connect", category: "quick_connect",
    prompt: "How often do you have meaningful conversations (not logistics)?",
    options: [
      { label: "Daily", value: "Daily", score: 3 },
      { label: "A few times a week", value: "A few times a week", score: 2 },
      { label: "Rarely", value: "Rarely", score: 1 },
      { label: "Non-existent", value: "Almost never", score: 0 },
    ],
  },
  {
    id: "q11", number: 11, section: "Quick Connect", category: "quick_connect",
    prompt: "How would you describe your day-to-day connection?",
    options: [
      { label: "We feel emotionally close, connected, and present with each other most days", value: "Close", score: 3 },
      { label: "We talk and connect, but it doesn\u2019t go deep", value: "Inconsistent", score: 2 },
      { label: "We love each other, but lately it feels like we are slowly drifting apart", value: "Distant", score: 1 },
      { label: "I rather avoid communication", value: "Roommates", score: 0 },
    ],
  },
  {
    id: "q12", number: 12, section: "Quick Connect", category: "quick_connect",
    prompt: "At the end of a typical day together, what does it usually look like?",
    options: [
      { label: "We connect, talk, and enjoy each other", value: "Connect", score: 3 },
      { label: "We talk a bit, but mostly about logistics", value: "Logistics", score: 2 },
      { label: "We\u2019re on our phones or doing separate things", value: "Separate", score: 1 },
      { label: "We barely interact", value: "Barely interact", score: 0 },
    ],
  },
  // SENSORY SYNC
  {
    id: "q13", number: 13, section: "Sensory Sync", category: "sensory_sync",
    prompt: "How satisfied are you with your physical intimacy?",
    options: [
      { label: "Very satisfied", value: "Very satisfied", score: 3 },
      { label: "It\u2019s okay", value: "Okay", score: 2 },
      { label: "It\u2019s lacking", value: "Lacking", score: 1 },
      { label: "It\u2019s almost non-existent", value: "Non-existent", score: 0 },
    ],
  },
  {
    id: "q14", number: 14, section: "Sensory Sync", category: "sensory_sync",
    prompt: "Do you feel desired by your partner?",
    options: [
      { label: "Yes, deeply", value: "Deeply", score: 3 },
      { label: "Sometimes", value: "Sometimes", score: 2 },
      { label: "Rarely", value: "Rarely", score: 1 },
      { label: "Not at all", value: "Not at all", score: 0 },
    ],
  },
  {
    id: "q14b", number: 14, section: "Sensory Sync", category: "sensory_sync",
    prompt: "Do you feel Desire for your partner?",
    options: [
      { label: "Yes, lot of desire, it\u2019s always present", value: "Lot of desire", score: 3 },
      { label: "A healthy portion I would say", value: "Healthy portion", score: 2 },
      { label: "Sometimes, I wish it was more", value: "Wish more", score: 1 },
      { label: "Honestly, it\u2019s rarely there", value: "Rarely there", score: 0 },
    ],
  },
  {
    id: "q15", number: 15, section: "Sensory Sync", category: "sensory_sync",
    prompt: "When you\u2019re physically close (hugging, kissing, touching), how does it feel?",
    options: [
      { label: "Juice and expanding", value: "Connected", score: 3 },
      { label: "Nice, but not exciting", value: "Nice", score: 2 },
      { label: "A bit distant or mechanical", value: "Distant", score: 1 },
      { label: "We rarely have that kind of contact", value: "Rare", score: 0 },
    ],
  },
  {
    id: "q15b", number: 15, section: "Sensory Sync", category: "sensory_sync",
    prompt: "How satisfied are you with the frequency of your physical intimacy?",
    options: [
      { label: "Very satisfied", value: "Very satisfied freq", score: 3 },
      { label: "Somewhat satisfied, I sure want more", value: "Somewhat satisfied freq", score: 2 },
      { label: "Not satisfied, I actually crave having it", value: "Not satisfied freq", score: 1 },
      { label: "Not happy at all, it\u2019s non-existent", value: "Non-existent freq", score: 0 },
    ],
  },
  // GRAND VISION
  {
    id: "q16", number: 16, section: "Grand Vision", category: "grand_vision",
    prompt: "Do you and your partner share a clear vision for your future?",
    options: [
      { label: "Yes, we\u2019re aligned", value: "Aligned", score: 3 },
      { label: "Somewhat", value: "Somewhat", score: 2 },
      { label: "Not really", value: "Not really", score: 1 },
      { label: "We\u2019ve never talked about it", value: "Never talked", score: 0 },
    ],
  },
  {
    id: "q17", number: 17, section: "Grand Vision", category: "grand_vision",
    prompt: "How often do you talk about your future together?",
    options: [
      { label: "Frequently", value: "Frequently", score: 3 },
      { label: "Occasionally", value: "Occasionally", score: 2 },
      { label: "Rarely", value: "Rarely", score: 1 },
      { label: "Never", value: "Never", score: 0 },
    ],
  },
  {
    id: "q18", number: 18, section: "Grand Vision", category: "grand_vision",
    prompt: "If a big life decision came up (move, kids, career shift), how would it go?",
    options: [
      { label: "We would probably sit down, talk it through, and make the decision together as a team", value: "Team", score: 3 },
      { label: "We would eventually get on the same page, but it would probably take a lot of conversations first", value: "Need time", score: 2 },
      { label: "It would likely create stress, conflict, or emotional distance between us", value: "Tension", score: 1 },
      { label: "Honestly, we avoid these kinds of conversations because we are not really aligned", value: "No alignment", score: 0 },
    ],
  },
  // SECTION 4 — EMOTIONAL HOOK
  {
    id: "q19", number: 19, section: "Emotional Hook",
    prompt: "If nothing changed in your relationship over the next 12 months\u2026 how would that feel?",
    options: [
      { label: "I\u2019d feel fulfilled and happy", value: "Fulfilled" },
      { label: "I\u2019d feel a bit disappointed", value: "Disappointed" },
      { label: "I\u2019d feel frustrated", value: "Frustrated" },
      { label: "I\u2019d feel deeply unhappy", value: "Deeply unhappy" },
    ],
  },
];

export function computeScores(answers) {
  const categorySum = {
    foundation: 0,
    quick_connect: 0,
    sensory_sync: 0,
    grand_vision: 0,
  };

  QUESTIONS.forEach((q) => {
    if (!q.category) return;
    const val = answers[q.id];
    const opt = q.options.find((o) => o.value === val);
    if (opt && typeof opt.score === "number") {
      categorySum[q.category] += opt.score;
    }
  });

  // Foundation/Quick Connect/Grand Vision: 3 questions × max 3pts → /9 normalized to /10.
  // Sensory Sync: 5 questions × max 3pts → /15 normalized to /10.
  const raw = {
    foundation: +(((categorySum.foundation / 9) * 10)).toFixed(1),
    quick_connect: +(((categorySum.quick_connect / 9) * 10)).toFixed(1),
    sensory_sync: +(((categorySum.sensory_sync / 15) * 10)).toFixed(1),
    grand_vision: +(((categorySum.grand_vision / 9) * 10)).toFixed(1),
  };

  const scores = { ...raw };
  const q4 = answers["q4"];
  const q4opt = QUESTIONS.find((q) => q.id === "q4").options.find(
    (o) => o.value === q4
  );
  if (q4opt && q4opt.weightCategory) {
    scores[q4opt.weightCategory] = Math.max(
      0,
      +(scores[q4opt.weightCategory] - 2).toFixed(1)
    );
  }

  const sorted = CATEGORY_ORDER.slice().sort((a, b) => scores[a] - scores[b]);

  return {
    scores,
    raw,
    primary: sorted[0],
    secondary: sorted[1],
  };
}

export const QUIZ_PROGRESS_KEY = "connexus_quiz_progress_v1";
export const LEAD_SUBMITTED_KEY = "connexus_lead_submitted_v1";
